import { hasPartyFeature, PARTY_PASS_DISPLAY_NAME } from "./party-entitlements";
import type {
  LiveResultCategory,
  PartyChatAccess,
  PartyChatActivity,
  PartyChatActivityKind,
  PartyChatListCue,
  PartyChatMessage,
  PartyChatPodiumMessage,
  PartyChatPreviewItem,
  PartyChatPreviewPodium,
  PartyChatPreviewUser,
  PartyChatPreviewWinner,
  PartyChatUserMessage,
  PartyChatWinnerMessage,
  PartyPlan,
  PodiumPass,
} from "./types/parties-api";

export const PARTY_CHAT_MESSAGE_MAX_LENGTH = 1000;
export const PARTY_CHAT_POLL_INTERVAL_MS = 5000;
export const PARTY_CHAT_PREVIEW_LIMIT = 3;
export const PARTY_CHAT_PREVIEW_BODY_MAX = 140;

export const PARTY_CHAT_TITLE = "Party chat";
export const PARTY_CHAT_SUBTITLE = "Chat with everyone in this party";
export const PARTY_CHAT_SEND_LABEL = "Send";
export const PARTY_CHAT_COMPOSER_PLACEHOLDER = "Message the party";
export const PARTY_CHAT_EMPTY_TITLE = "No messages yet";
export const PARTY_CHAT_EMPTY_DESCRIPTION =
  "Be the first to say something. Party chat is just for this group.";
export const PARTY_CHAT_UPGRADE_TITLE = "Party chat is a Party Pass feature";
export const PARTY_CHAT_UPGRADE_DESCRIPTION =
  "Unlock Party Pass once to chat with everyone in this party.";
export const PARTY_CHAT_UPGRADE_MESSAGE = `Party chat is included with ${PARTY_PASS_DISPLAY_NAME}.`;
export const PARTY_CHAT_NOT_MEMBER_MESSAGE =
  "You are not a member of this party";
export const PARTY_CHAT_NOT_FOUND_MESSAGE = "Party not found";

export type PartyChatMessageValidation =
  | { ok: true; body: string }
  | { ok: false; error: string };

export function getPartyChatAccess(
  plan: PartyPlan | string | null | undefined,
): PartyChatAccess {
  return hasPartyFeature(plan, "chat") ? "allowed" : "upgrade_required";
}

export type PartyChatDestinationVariant = "live" | "upgrade";

export function getPartyChatDestinationVariant(
  plan: PartyPlan | string | null | undefined,
): PartyChatDestinationVariant {
  return getPartyChatAccess(plan) === "allowed" ? "live" : "upgrade";
}

export function shouldShowShowNightChatEntry(input: {
  plan: PartyPlan | string | null | undefined;
  showHasStarted: boolean;
  showIsComplete: boolean;
}): boolean {
  return (
    getPartyChatAccess(input.plan) === "allowed" &&
    input.showHasStarted &&
    !input.showIsComplete
  );
}

export function validatePartyChatMessage(
  value: unknown,
): PartyChatMessageValidation {
  if (typeof value !== "string") {
    return { ok: false, error: "Message is required" };
  }

  const body = value.trim();
  if (!body) {
    return { ok: false, error: "Message is required" };
  }

  if (body.length > PARTY_CHAT_MESSAGE_MAX_LENGTH) {
    return {
      ok: false,
      error: `Message must be ${PARTY_CHAT_MESSAGE_MAX_LENGTH} characters or less`,
    };
  }

  return { ok: true, body };
}

export function getPartyMemberDisplayName(
  user:
    | {
        username?: string | null;
        name?: string | null;
        email?: string | null;
      }
    | null
    | undefined,
): string {
  const username = user?.username?.trim();
  if (username) {
    return username;
  }

  const name = user?.name?.trim();
  if (name) {
    return name;
  }

  const email = user?.email?.trim();
  if (email) {
    return email;
  }

  return "Member";
}

export function serializePartyChatMessage(
  message: {
    id: string;
    partyId: string;
    userId: string;
    body: string;
    createdAt: Date | string;
  },
  sender: {
    id?: string;
    username?: string | null;
    name?: string | null;
    email?: string | null;
    imageUrl?: string | null;
  } | null,
): PartyChatUserMessage {
  const createdAt =
    message.createdAt instanceof Date
      ? message.createdAt.toISOString()
      : message.createdAt;

  return {
    kind: "user",
    id: message.id,
    partyId: message.partyId,
    userId: message.userId,
    body: message.body,
    createdAt,
    sender: {
      id: sender?.id ?? message.userId,
      name: getPartyMemberDisplayName(sender),
      imageUrl: sender?.imageUrl ?? null,
    },
  };
}

export function isPartyChatUserMessage(
  message: PartyChatMessage,
): message is PartyChatUserMessage {
  return message.kind === "user";
}

export function isPartyChatWinnerMessage(
  message: PartyChatMessage,
): message is PartyChatWinnerMessage {
  return message.kind === "winner";
}

export function isPartyChatPodiumMessage(
  message: PartyChatMessage,
): message is PartyChatPodiumMessage {
  return message.kind === "podium";
}

export function partyChatPodiumMessageId(
  categoryId: string,
  climberUserId: string,
  toPlace: number,
): string {
  return `podium:${categoryId}:${toPlace}:${climberUserId}`;
}

export function serializePartyChatPodiumMessage(
  partyId: string,
  pass: PodiumPass,
): PartyChatPodiumMessage {
  return {
    kind: "podium",
    id: partyChatPodiumMessageId(
      pass.categoryId,
      pass.climberUserId,
      pass.toPlace,
    ),
    partyId,
    createdAt: pass.announcedAt,
    pass,
  };
}

const CHAT_TIMELINE_KIND_ORDER: Record<PartyChatActivityKind, number> = {
  winner: 0,
  podium: 1,
  user: 2,
};

function comparePartyChatMessages(
  left: PartyChatMessage,
  right: PartyChatMessage,
): number {
  const timeDiff =
    chatMessageTimeMs(left.createdAt) - chatMessageTimeMs(right.createdAt);
  if (timeDiff !== 0) {
    return timeDiff;
  }
  const kindDiff =
    CHAT_TIMELINE_KIND_ORDER[left.kind] - CHAT_TIMELINE_KIND_ORDER[right.kind];
  if (kindDiff !== 0) {
    return kindDiff;
  }
  return left.id.localeCompare(right.id);
}

export function partyChatWinnerMessageId(categoryId: string): string {
  return `winner:${categoryId}`;
}

export function serializePartyChatWinnerMessage(
  partyId: string,
  winner: LiveResultCategory,
): PartyChatWinnerMessage {
  return {
    kind: "winner",
    id: partyChatWinnerMessageId(winner.categoryId),
    partyId,
    createdAt: winner.announcedAt,
    winner,
  };
}

export function partyChatTimeMs(iso: string): number {
  const time = Date.parse(iso);
  return Number.isFinite(time) ? time : Number.NaN;
}

function chatMessageTimeMs(iso: string): number {
  const time = partyChatTimeMs(iso);
  return Number.isFinite(time) ? time : 0;
}

export function toPartyChatActivity(
  message: PartyChatMessage,
): PartyChatActivity {
  if (isPartyChatUserMessage(message)) {
    return {
      at: message.createdAt,
      kind: "user",
      userId: message.userId,
    };
  }

  if (isPartyChatPodiumMessage(message)) {
    return {
      at: message.createdAt,
      kind: "podium",
      userId: null,
    };
  }

  return {
    at: message.createdAt,
    kind: "winner",
    userId: null,
  };
}

export function pickLaterPartyChatActivity(
  left: PartyChatActivity | null,
  right: PartyChatActivity | null,
): PartyChatActivity | null {
  if (!left) {
    return right;
  }
  if (!right) {
    return left;
  }

  const leftMs = chatMessageTimeMs(left.at);
  const rightMs = chatMessageTimeMs(right.at);
  if (leftMs !== rightMs) {
    return leftMs > rightMs ? left : right;
  }

  if (left.kind !== right.kind) {
    return CHAT_TIMELINE_KIND_ORDER[left.kind] >
      CHAT_TIMELINE_KIND_ORDER[right.kind]
      ? left
      : right;
  }

  return right;
}

export function getLatestPartyChatActivity(
  messages: readonly PartyChatMessage[],
): PartyChatActivity | null {
  return messages.reduce<PartyChatActivity | null>(
    (latest, message) =>
      pickLaterPartyChatActivity(latest, toPartyChatActivity(message)),
    null,
  );
}

export function hasUnreadPartyChatActivity(input: {
  access: PartyChatAccess | null;
  latestActivity: PartyChatActivity | null;
  lastReadAt: string | null;
  currentUserId: string | null;
}): boolean {
  if (input.access !== "allowed" || !input.latestActivity) {
    return false;
  }

  if (
    input.latestActivity.kind === "user" &&
    input.latestActivity.userId &&
    input.latestActivity.userId === input.currentUserId
  ) {
    return false;
  }

  const lastReadMs = input.lastReadAt ? partyChatTimeMs(input.lastReadAt) : Number.NaN;
  if (!Number.isFinite(lastReadMs)) {
    return true;
  }

  return chatMessageTimeMs(input.latestActivity.at) > lastReadMs;
}

export function mergeWinnerAnnouncementsIntoChat(
  partyId: string,
  messages: readonly PartyChatMessage[],
  winners: readonly LiveResultCategory[],
  passes: readonly PodiumPass[] = [],
): PartyChatMessage[] {
  const userMessages = messages.filter(isPartyChatUserMessage);
  const winnerMessages = winners.map((winner) =>
    serializePartyChatWinnerMessage(partyId, winner),
  );
  const podiumMessages = passes.map((pass) =>
    serializePartyChatPodiumMessage(partyId, pass),
  );

  return [...userMessages, ...winnerMessages, ...podiumMessages].sort(
    comparePartyChatMessages,
  );
}

export function truncatePartyChatPreviewBody(
  body: string,
  max = PARTY_CHAT_PREVIEW_BODY_MAX,
): string {
  const collapsed = body.replace(/\s+/g, " ").trim();
  if (max <= 0) {
    return "";
  }
  if (collapsed.length <= max) {
    return collapsed;
  }
  if (max <= 3) {
    return collapsed.slice(0, max);
  }
  return `${collapsed.slice(0, max - 3).trimEnd()}...`;
}

export function toPartyChatPreviewItem(
  message: PartyChatMessage,
): PartyChatPreviewItem {
  if (isPartyChatUserMessage(message)) {
    return {
      kind: "user",
      id: message.id,
      at: message.createdAt,
      userId: message.userId,
      senderName: message.sender.name,
      body: truncatePartyChatPreviewBody(message.body),
    };
  }

  if (isPartyChatPodiumMessage(message)) {
    return toPartyChatPodiumPreview(message.pass);
  }

  return {
    kind: "winner",
    id: message.id,
    at: message.createdAt,
    categoryName: message.winner.categoryName,
    winnerName: message.winner.winnerName,
  };
}

export function toPartyChatPodiumPreview(
  pass: PodiumPass,
): PartyChatPreviewPodium {
  return {
    kind: "podium",
    id: partyChatPodiumMessageId(
      pass.categoryId,
      pass.climberUserId,
      pass.toPlace,
    ),
    at: pass.announcedAt,
    climberName: pass.climberName,
    passedName: pass.passedName,
    toPlace: pass.toPlace,
    variant: pass.variant,
    style: pass.style,
    ...(pass.otherClimbers ? { otherClimbers: pass.otherClimbers } : {}),
  };
}

export function selectLatestPodiumPreview(
  passes: readonly PodiumPass[],
): PartyChatPreviewPodium | null {
  let latest: PartyChatPreviewPodium | null = null;
  for (const pass of passes) {
    const preview = toPartyChatPodiumPreview(pass);
    if (!latest) {
      latest = preview;
      continue;
    }
    const timeDiff = chatMessageTimeMs(preview.at) - chatMessageTimeMs(latest.at);
    if (timeDiff > 0 || (timeDiff === 0 && preview.id > latest.id)) {
      latest = preview;
    }
  }
  return latest;
}

export function selectRecentPartyChatPreviews(
  messages: readonly PartyChatMessage[],
  limit = PARTY_CHAT_PREVIEW_LIMIT,
): PartyChatPreviewItem[] {
  const safeLimit = Number.isFinite(limit) ? Math.floor(limit) : 0;
  if (safeLimit <= 0) {
    return [];
  }
  return messages.slice(-safeLimit).map(toPartyChatPreviewItem);
}

export function buildPartyChatSummaryTimeline(input: {
  partyId: string;
  userMessages: readonly PartyChatUserMessage[];
  winners: readonly LiveResultCategory[];
  passes?: readonly PodiumPass[];
  limit?: number;
}): {
  latestActivity: PartyChatActivity | null;
  recent: PartyChatPreviewItem[];
} {
  const merged = mergeWinnerAnnouncementsIntoChat(
    input.partyId,
    input.userMessages,
    input.winners,
    input.passes ?? [],
  );
  return {
    latestActivity: getLatestPartyChatActivity(merged),
    recent: selectRecentPartyChatPreviews(merged, input.limit),
  };
}

export function buildPartyChatListCue(
  userPreview: PartyChatPreviewUser | null,
  winnerPreview: PartyChatPreviewWinner | null,
  podiumPreview: PartyChatPreviewPodium | null = null,
): PartyChatListCue {
  const candidates: Array<{
    activity: PartyChatActivity;
    preview: PartyChatPreviewItem;
  }> = [];
  if (userPreview) {
    candidates.push({
      activity: { at: userPreview.at, kind: "user", userId: userPreview.userId },
      preview: userPreview,
    });
  }
  if (winnerPreview) {
    candidates.push({
      activity: { at: winnerPreview.at, kind: "winner", userId: null },
      preview: winnerPreview,
    });
  }
  if (podiumPreview) {
    candidates.push({
      activity: { at: podiumPreview.at, kind: "podium", userId: null },
      preview: podiumPreview,
    });
  }

  let latest: (typeof candidates)[number] | null = null;
  for (const candidate of candidates) {
    const picked = pickLaterPartyChatActivity(
      latest?.activity ?? null,
      candidate.activity,
    );
    if (picked === candidate.activity) {
      latest = candidate;
    }
  }

  if (!latest) {
    return { latestActivity: null, latestPreview: null };
  }

  return {
    latestActivity: latest.activity,
    latestPreview: latest.preview,
  };
}
